import { Routes } from '@angular/router'

import { stepModeRoutes } from '@yoshu-lib/features/interaction-modes/step-mode/step-mode.routes'

import { groupGuard } from '@yoshu-lib/guards/group.guard'

import { authGuard } from '@coty/core/auth/auth.guard'
import { redirectIfAuthGuard } from '@coty/core/auth/redirect-if-auth.guard'

import { environment } from '@coty/env/environment'

import { adminGuard } from './core/auth/admin.guard'
import { centerlineGuard } from './core/auth/centerline.guard'
import { workbenchGuard } from './core/auth/workbench.guard'

export const routes: Routes = [
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layouts/main/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'incident'
      },

      {
        path: 'protocols',
        canActivate: [authGuard, groupGuard('protocol_user')],
        data: { titleKey: 'admin.sidebar.protocols' },
        children: stepModeRoutes
      },

      {
        path: 'incident',
        canActivate: [authGuard],
        data: { titleKey: 'incident.new_incident' },
        loadComponent: () =>
          import('./pages/incident/incident-start.component').then((m) => m.IncidentStartComponent)
      },
      {
        // Agentic protocol workbench (skeleton): three-pane troubleshooting
        // surface driven by the `coty-troubleshooter` agent. Declared before
        // `incident/:incidentId` so the router does not try to resolve
        // `workbench` as one of that route's children.
        path: 'incident/:incidentId/workbench',
        // `workbenchGuard`: only the troubleshooter's group may start a
        // session (yoshu#3565).
        canActivate: [authGuard, groupGuard('protocol_user'), workbenchGuard],
        loadComponent: () =>
          import('./pages/workbench/workbench.component').then((m) => m.WorkbenchComponent)
      },
      {
        path: 'incident/:incidentId',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/incident/incident-detail.component').then(
            (m) => m.IncidentDetailComponent
          ),
        children: [
          {
            path: 'session/:sessionId',
            loadComponent: () =>
              import('@yoshu-lib/features/interaction-modes/step-mode/step-mode.component').then(
                (m) => m.StepModeComponent
              )
          },
          {
            path: 'chat/:interactionId',
            loadComponent: () =>
              import('@yoshu-lib/features/interaction-modes/free-mode/free-mode.component').then(
                (m) => m.FreeModeComponent
              ),
            data: { workflow: 'rag', hideHeader: true }
          }
        ]
      },

      {
        path: 'incidents',
        canActivate: [authGuard],
        data: { titleKey: 'incident.list_title' },
        loadComponent: () =>
          import('./pages/incidents-list/incidents-list.component').then(
            (m) => m.IncidentsListComponent
          )
      },

      {
        path: 'centerline',
        canActivate: [authGuard, centerlineGuard],
        data: { titleKey: 'centerline.title' },
        loadComponent: () =>
          import('./pages/centerline/centerline-list.component').then(
            (m) => m.CenterlineListComponent
          )
      },
      {
        path: 'centerline/:sheetId',
        canActivate: [authGuard, centerlineGuard],
        loadComponent: () =>
          import('./pages/centerline/centerline-matrix.component').then(
            (m) => m.CenterlineMatrixComponent
          )
      },

      {
        path: 'sessions',
        canActivate: [authGuard, groupGuard('protocol_user')],
        data: { titleKey: 'sessions_list.title' },
        loadComponent: () =>
          import('./pages/sessions-list/sessions-list.component').then(
            (m) => m.SessionsListComponent
          )
      }
    ]
  },

  ...(environment.features['module.admin']
    ? [
        {
          path: 'admin',
          canActivate: [adminGuard],
          loadComponent: () =>
            import('./pages/admin/admin.component').then((m) => m.AdminComponent),
          children: [
            {
              path: '',
              loadChildren: () =>
                import('@yoshu-lib/features/admin/admin.routes').then((m) => m.routes)
            }
          ]
        } satisfies Routes[number]
      ]
    : []),

  {
    path: 'login',
    canActivate: [redirectIfAuthGuard],
    data: { titleKey: 'login.title' },
    loadComponent: () => import('@yoshu-lib/ui/login').then((m) => m.LoginComponent)
  },

  {
    path: 'password-change',
    data: { titleKey: 'password_change.title' },
    loadComponent: () =>
      import('@yoshu-lib/ui/password-change').then((m) => m.PasswordChangeComponent)
  },

  {
    path: 'reset-password',
    data: { titleKey: 'reset_password.title' },
    loadComponent: () =>
      import('@yoshu-lib/ui/reset-password').then((m) => m.ResetPasswordComponent)
  },

  {
    path: '**',
    redirectTo: ''
  }
]
